import type { CalendarEventType } from "../../../../types/calendar";
import type { SalonService } from "../../../../types/salon";
import { BOOKING_TEMP_ID_PREFIX, ServiceRow } from "./bookingTypes";
import { adjustedBaseDuration, serviceLength } from "./draftModel";

/**
 * Calendar temp events for the draft: one dashed (isTransient) block per row
 * that has a time, on the booking day. A service-less row renders as a
 * "New service" block of its default length so it can be placed before a
 * service is picked. The active row is highlighted (isActiveTransient).
 * A reschedule passes the client's name so the blocks read like the
 * appointment they replace on the grid. Lengths include the row's
 * adjustment minutes (as booked events do: transactionUtils adds the sub
 * line item deltas).
 */
export const buildTempEvents = ({
  rows,
  day,
  activeUid,
  clientUuid,
  services,
  fallbackResourceId,
  client,
}: {
  rows: ServiceRow[];
  day: string;
  activeUid: number;
  clientUuid: string;
  services: SalonService[];
  fallbackResourceId: string;
  client?: { firstName: string; lastName: string };
}): CalendarEventType[] => {
  const [y, mo, d] = day.split("-").map(Number);
  return rows
    .filter((r) => r.time)
    .map((r) => {
      const svc = services.find((s) => s.id === r.salonServiceId);
      const [h, mi] = r.time.split(":").map(Number);
      // Salon wall-clock as browser-local: how the grid places every event.
      const start = new Date(y, mo - 1, d, h, mi);
      const lenMins = serviceLength(r);
      const end = new Date(start.getTime() + Math.max(lenMins, 1) * 60000);
      return {
        id: `${BOOKING_TEMP_ID_PREFIX}${r.uid}`,
        title: svc?.name || "New service",
        start,
        end,
        backgroundColor: svc?.default_calendar_event_color || "#36AFC9",
        duration: lenMins,
        baseDuration: adjustedBaseDuration(r),
        processingTime: r.processingTime,
        finishingTime: r.finishingTime,
        bufferTime: r.bufferTime,
        enableDoubleBooking: false,
        price: r.price,
        firstName: client?.firstName ?? "",
        lastName: client?.lastName ?? "",
        phone: "",
        email: "",
        salonServiceId: r.salonServiceId,
        employeeName: "",
        employeeId: r.employeeId,
        resourceId: r.employeeId || fallbackResourceId,
        status: "",
        customerNotes: "",
        isOptedInReminder: false,
        clientUuid,
        transactionId: "",
        transactionStatus: "",
        payments: [],
        isTransient: true,
        isActiveTransient: r.uid === activeUid,
      } as unknown as CalendarEventType;
    });
};

/** The draft row uid of one of our temp events, or null for any other event. */
export const tempEventRowUid = (event: unknown): number | null => {
  const e = event as { id?: unknown; isTransient?: boolean } | null;
  if (!e?.isTransient || typeof e.id !== "string") return null;
  if (!e.id.startsWith(BOOKING_TEMP_ID_PREFIX)) return null;
  const uid = Number(e.id.slice(BOOKING_TEMP_ID_PREFIX.length));
  return Number.isNaN(uid) ? null : uid;
};

/** While the panel is open only temp events are draggable. */
export const isTempEvent = (event: CalendarEventType): boolean =>
  tempEventRowUid(event) !== null;

/** Nothing is resizable while the panel is open. */
export const notResizable = (): boolean => false;

/**
 * The calendar events while the booking panel is open: the draft's temp
 * events added, and in reschedule ("edit") mode the source transaction's own
 * events hidden so its temp events stand in their place (no duplicate).
 */
export const eventsWithDraft = (
  events: CalendarEventType[],
  tempEvents: CalendarEventType[],
  hiddenTransactionId: string | null,
): CalendarEventType[] => [
  ...(hiddenTransactionId
    ? events.filter(
        (e) =>
          (e as { transactionId?: string }).transactionId !==
          hiddenTransactionId,
      )
    : events),
  ...tempEvents,
];
